import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Play, Star, CheckCircle, Zap, Shield } from 'lucide-react';

interface HeroSectionProps {
  onGetStarted: () => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({ onGetStarted }) => {
  const { t } = useTranslation();

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden pt-16">
      {/* Background blobs */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-20 -left-32 w-96 h-96 bg-primary-200/40 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-20 -right-32 w-96 h-96 bg-secondary-200/40 rounded-full blur-3xl animate-float" style={{ animationDelay: '3s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-accent-100/30 rounded-full blur-3xl" />
        {/* Extra decorative dots */}
        <div className="absolute top-32 right-[15%] w-4 h-4 bg-accent-400 rounded-full animate-float opacity-60" style={{ animationDelay: '2s' }} />
        <div className="absolute bottom-40 left-[10%] w-3 h-3 bg-secondary-400 rounded-full animate-float opacity-60" style={{ animationDelay: '4s' }} />
        <div className="absolute top-[60%] right-[8%] w-2 h-2 bg-primary-400 rounded-full animate-float opacity-50" style={{ animationDelay: '1s' }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: Copy */}
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary-50 border border-primary-200 rounded-full text-sm text-primary-700 font-medium mb-6">
              <span className="w-2 h-2 rounded-full bg-secondary animate-pulse" />
              {t('landing.hero.badge')}
            </div>

            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight mb-6">
              {t('landing.hero.title')}{' '}
              <span className="gradient-text">{t('landing.hero.titleHighlight')}</span>
            </h1>

            <p className="text-lg text-gray-600 mb-8 max-w-xl mx-auto lg:mx-0">
              {t('landing.hero.subtitle')}
            </p>

            {/* Feature pills */}
            <div className="flex flex-wrap items-center gap-3 justify-center lg:justify-start mb-8">
              {[
                { icon: Zap, text: t('landing.hero.pillAiGenerated') },
                { icon: Shield, text: t('landing.hero.pillSecureScoring') },
                { icon: CheckCircle, text: t('landing.hero.pillNoSignUp') },
              ].map(({ icon: Icon, text }) => (
                <span key={text} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-full text-xs font-medium text-gray-600 shadow-sm">
                  <Icon size={14} className="text-secondary" />
                  {text}
                </span>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start mb-10">
              <button
                onClick={onGetStarted}
                className="flex items-center gap-2 px-8 py-4 bg-primary text-white rounded-2xl font-bold text-lg hover:bg-primary-600 transition shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 hover:-translate-y-0.5"
              >
                <GoogleIcon />
                {t('common.getStartedFree')}
                <ArrowRight size={20} />
              </button>
              <a
                href="#how-it-works"
                className="flex items-center gap-2 px-6 py-4 text-gray-700 font-medium hover:text-primary transition"
              >
                <Play size={18} className="text-primary" />
                {t('landing.hero.seeHowItWorks')}
              </a>
            </div>

            {/* Social proof: avatars + rating */}
            <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
              {/* Avatar stack */}
              <div className="flex -space-x-3">
                {['#7c3aed', '#14b8a6', '#f59e0b', '#ef4444', '#6d28d9'].map((color, i) => (
                  <div
                    key={color}
                    className="w-10 h-10 rounded-full border-2 border-white flex items-center justify-center text-white text-xs font-bold shadow-sm"
                    style={{ backgroundColor: color }}
                  >
                    {['MR', 'KL', 'JS', 'AP', 'TC'][i]}
                  </div>
                ))}
              </div>
              <div className="text-left">
                <div className="flex items-center gap-1 mb-0.5">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} size={14} className="fill-accent text-accent" />
                  ))}
                  <span className="text-sm font-bold text-gray-800 ml-1">{t('landing.hero.rating')}</span>
                </div>
                <p className="text-xs text-gray-500" dangerouslySetInnerHTML={{ __html: t('landing.hero.socialProof') }} />
              </div>
            </div>
          </div>

          {/* Right: Exam mockup + floating elements */}
          <div className="relative animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
            {/* Main mockup card */}
            <div className="bg-white rounded-3xl shadow-2xl p-6 border border-gray-100 relative z-10">
              {/* Mockup header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-danger-500" />
                  <div className="w-3 h-3 rounded-full bg-accent" />
                  <div className="w-3 h-3 rounded-full bg-success-500" />
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <span className="px-2 py-0.5 bg-primary-50 text-primary rounded-full font-medium">{t('exam.questionOf', { current: 3, total: 10 })}</span>
                  <span className="px-2 py-0.5 bg-accent-50 text-accent-700 rounded-full font-medium">8s</span>
                </div>
              </div>

              {/* Mockup progress */}
              <div className="w-full bg-gray-100 rounded-full h-2 mb-6">
                <div className="bg-gradient-to-r from-primary to-secondary h-2 rounded-full w-[30%]" />
              </div>

              {/* Mockup question */}
              <p className="font-heading font-bold text-gray-900 mb-4">
                {t('landing.hero.mockupQuestion')}
              </p>

              {/* Mockup options */}
              <div className="space-y-3">
                {(t('landing.hero.mockupOptions', { returnObjects: true }) as string[]).map((opt, optIdx) => (
                  <div
                    key={opt}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm ${
                      optIdx === 1
                        ? 'bg-primary text-white shadow-md shadow-primary/20'
                        : 'bg-gray-50 text-gray-700'
                    }`}
                  >
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                      optIdx === 1 ? 'bg-white/20 text-white' : 'bg-white text-gray-500'
                    }`}>
                      {String.fromCharCode(65 + optIdx)}
                    </span>
                    {opt}
                    {optIdx === 1 && <CheckCircle size={16} className="ml-auto" />}
                  </div>
                ))}
              </div>
            </div>

            {/* Floating: AI Generated badge */}
            <div className="absolute -top-4 -right-4 bg-accent text-white px-4 py-2 rounded-xl font-heading font-bold text-sm shadow-lg animate-float z-20" style={{ animationDelay: '1s' }}>
              <Zap size={14} className="inline mr-1" />
              {t('landing.hero.pillAiGenerated')}
            </div>

            {/* Floating: Score card */}
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 border border-gray-100 animate-float z-20" style={{ animationDelay: '2s' }}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-success-500 to-secondary flex items-center justify-center text-white font-heading font-extrabold text-lg">
                  92
                </div>
                <div>
                  <p className="text-xs text-gray-500">{t('landing.hero.averageScore')}</p>
                  <p className="text-sm font-bold text-gray-900">{t('landing.hero.excellent')}</p>
                </div>
              </div>
            </div>

            {/* Floating: Live results notification */}
            <div className="absolute top-1/2 -right-10 lg:-right-16 bg-white rounded-2xl shadow-xl p-3 border border-gray-100 animate-float z-20 hidden sm:block" style={{ animationDelay: '0.5s' }}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                  <CheckCircle size={16} className="text-primary" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-800">{t('landing.hero.scoredNotification')}</p>
                  <p className="text-[10px] text-gray-400">{t('common.justNow')}</p>
                </div>
              </div>
            </div>

            {/* Floating: Students count */}
            <div className="absolute top-8 -left-8 lg:-left-12 bg-white rounded-2xl shadow-xl p-3 border border-gray-100 animate-float z-20 hidden sm:block" style={{ animationDelay: '3.5s' }}>
              <div className="flex items-center gap-2">
                <div className="flex -space-x-1.5">
                  {['#7c3aed', '#14b8a6', '#f59e0b'].map((c) => (
                    <div key={c} className="w-6 h-6 rounded-full border-2 border-white" style={{ backgroundColor: c }} />
                  ))}
                </div>
                <p className="text-xs font-semibold text-gray-800">{t('landing.hero.takingNow', { count: 24 })}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Trusted by logos */}
        <div className="mt-20 text-center">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-widest mb-6">{t('landing.hero.trustedBy')}</p>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4 opacity-40 grayscale">
            {[
              { name: 'Harvard', icon: '🎓' },
              { name: 'Stanford', icon: '📚' },
              { name: 'MIT', icon: '🔬' },
              { name: 'Oxford', icon: '🏛️' },
              { name: 'Google', icon: '🔎' },
              { name: 'Microsoft', icon: '💻' },
            ].map((org) => (
              <div key={org.name} className="flex items-center gap-2 text-gray-700">
                <span className="text-2xl">{org.icon}</span>
                <span className="font-heading font-bold text-lg">{org.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="currentColor" fillOpacity="0.8"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="currentColor" fillOpacity="0.6"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 001 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z" fill="currentColor" fillOpacity="0.7"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="currentColor" fillOpacity="0.9"/>
  </svg>
);

export default HeroSection;
